import { CommonModule } from '@angular/common';
import { Component, HostListener } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { LogoutService } from 'app/core/auth/logout.service';
import { ApiService } from 'app/shared/api.service';
import Swal from 'sweetalert2';

interface UserPayload {
    name: string;
    username: string;
    email: string;
    password?: string;
    status: 'active' | 'inactive' | 'pending';
    role_id: string | null;
}

interface User {
    id: string;
    name: string;
    username: string;
    email: string;
    status: string;
    roles: Array<{ id: string; name: string }>;
    gates?: Array<{ id: string; name: string; code: string }>;
}

interface Gate {
    id: string;
    name: string;
    code: string;
    type: 'entry' | 'exit' | 'both';
    status: 'active' | 'inactive';
    location_id: string;
}

@Component({
    selector: 'app-user',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './user.component.html',
    styleUrl: './user.component.scss',
})
export class UserComponent {
    isDropdownOpen = false;
    isLoggingOut = false;
    isModalOpen = false;
    isEditMode = false;
    isSubmitting = false;
    isGateModalOpen = false;
    isAssigningGates = false;

    User: User[] = [];
    roles: any[] = [];
    gates: Gate[] = [];
    selectedGateIds: string[] = [];
    selectedUserForGate: User | null = null;

    userForm: UserPayload = {
        name: '',
        username: '',
        email: '',
        password: '',
        status: 'active',
        role_id: null,
    };

    selectedUserId: string = '';

    pagination = {
        current_page: 1,
        total_page: 1,
        per_page: 10,
        total: 0,
    };

    constructor(
        private logoutService: LogoutService,
        private apiService: ApiService,
        private router: Router
    ) { }

    ngOnInit(): void {
        this.getUser(1);
        this.getRoles();
    }

    // ==================== CRUD OPERATIONS ====================

    // GET - List Users
    async getUser(page: number = 1): Promise<void> {
        try {
            const response = await this.apiService.get(
                `/api/v1/manage/user/list?page=${page}&per_page=${this.pagination.per_page}`
            );

            this.User = response.data.list || [];

            if (response.pagination) {
                this.pagination = response.pagination;
            } else {
                this.pagination.current_page = page;
            }

        } catch (error) {
            console.error('Error fetching users:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Terjadi kesalahan saat memuat data user',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false,
            });
        }
    }

    // Pagination Methods
    goToPage(page: number | string): void {
        if (typeof page !== 'number') return;
        if (page >= 1 && page <= this.pagination.total_page) {
            this.getUser(page);
        }
    }

    nextPage(): void {
        if (this.pagination.current_page < this.pagination.total_page) {
            this.getUser(this.pagination.current_page + 1);
        }
    }

    previousPage(): void {
        if (this.pagination.current_page > 1) {
            this.getUser(this.pagination.current_page - 1);
        }
    }

    // getters for pagination view
    get startIndex(): number {
        return (this.pagination.current_page - 1) * this.pagination.per_page + 1;
    }

    get endIndex(): number {
        return Math.min(
            this.pagination.current_page * this.pagination.per_page,
            this.pagination.total
        );
    }

    get visiblePages(): (number | string)[] {
        const total = this.pagination.total_page;
        const current = this.pagination.current_page;
        const pages: (number | string)[] = [];

        if (total <= 7) {
            for (let i = 1; i <= total; i++) {
                pages.push(i);
            }
        } else {
            if (current <= 4) {
                // Pattern: 1 2 3 4 5 ... total
                for (let i = 1; i <= 5; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(total);
            } else if (current >= total - 3) {
                // Pattern: 1 ... total-4 to total
                pages.push(1);
                pages.push('...');
                for (let i = total - 4; i <= total; i++) {
                    pages.push(i);
                }
            } else {
                // Pattern: 1 ... current-1 current current+1 ... total
                pages.push(1);
                pages.push('...');
                for (let i = current - 1; i <= current + 1; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(total);
            }
        }
        return pages;
    }

    // GET - Single User (untuk edit)
    async getUserById(userId: string): Promise<void> {
        try {
            const response = await this.apiService.get(
                `/api/v1/manage/user/${userId}`
            );
            // console.log(response);

            // Populate form dengan data user
            this.userForm = {
                name: response.data.name,
                username: response.data.username,
                email: response.data.email,
                status: response.data.status,
                role_id: response.data.roles?.[0]?.id || null,
            };

            this.selectedUserId = userId;
            this.isEditMode = true;
            this.isModalOpen = true;
        } catch (error) {
            console.error('Error fetching user:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Gagal memuat data user',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false,
            });
        }
    }

    // POST - Create User
    async createUser(): Promise<void> {
        if (!this.validateForm()) {
            return;
        }

        this.isSubmitting = true;

        try {
            await this.apiService.post('/api/v1/manage/user', this.userForm);

            await Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'User berhasil ditambahkan',
                confirmButtonColor: '#28a745',
                timer: 2000,
                showConfirmButton: false,
            });

            this.closeModal();
            this.getUser();
        } catch (error: any) {
            console.error('Error creating user:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Gagal',
                text: error?.error?.message || 'Gagal menambahkan user',
                confirmButtonColor: '#dc3545',
            });
        } finally {
            this.isSubmitting = false;
        }
    }

    // PUT/PATCH - Update User
    async updateUser(): Promise<void> {
        if (!this.validateForm(true)) {
            return;
        }

        this.isSubmitting = true;

        try {
            // Jangan kirim password jika kosong saat update
            const payload: any = { ...this.userForm };
            if (!payload.password) {
                delete payload.password;
            }

            await this.apiService.put(
                `/api/v1/manage/user/${this.selectedUserId}`,
                payload
            );

            await Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'User berhasil diupdate',
                confirmButtonColor: '#28a745',
                timer: 2000,
                showConfirmButton: false,
            });

            this.closeModal();
            this.getUser();
        } catch (error: any) {
            console.error('Error updating user:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Gagal',
                text: error?.error?.message || 'Gagal mengupdate user',
                confirmButtonColor: '#dc3545',
            });
        } finally {
            this.isSubmitting = false;
        }
    }

    // DELETE - Delete User
    async deleteUser(userId: string, userName: string): Promise<void> {
        const result = await Swal.fire({
            title: 'Konfirmasi Hapus',
            text: `Apakah Anda yakin ingin menghapus user "${userName}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Ya, Hapus',
            cancelButtonText: 'Batal',
        });

        if (result.isConfirmed) {
            try {
                await this.apiService.delete('/api/v1/manage/user', userId);

                await Swal.fire({
                    icon: 'success',
                    title: 'Berhasil',
                    text: 'User berhasil dihapus',
                    confirmButtonColor: '#28a745',
                    timer: 2000,
                    showConfirmButton: false,
                });

                this.getUser();
            } catch (error: any) {
                console.error('Error deleting user:', error);
                await Swal.fire({
                    icon: 'error',
                    title: 'Gagal',
                    text: error?.error?.message || 'Gagal menghapus user',
                    confirmButtonColor: '#dc3545',
                });
            }
        }
    }

    // ==================== GATE ASSIGNMENT OPERATIONS ====================

    // GET - List Gates (untuk assign modal)
    async getGates(): Promise<void> {
        try {
            const response = await this.apiService.get(
                '/api/v1/tenant/manage/gate/list?page=1&per_page=10'
            );
            this.gates = response.list || response.data || [];
        } catch (error) {
            console.error('Error fetching gates:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.error?.message || 'Gagal memuat data gate',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false,
            });
        }
    }

    // Open Gate Assignment Modal
    async openGateModal(user: User): Promise<void> {
        this.selectedUserForGate = user;

        // Load gates
        await this.getGates();

        // Pre-select gates that user already has
        this.selectedGateIds = user.gates?.map((gate) => gate.id) || [];

        this.isGateModalOpen = true;
    }

    // Close Gate Assignment Modal
    closeGateModal(): void {
        this.isGateModalOpen = false;
        this.selectedUserForGate = null;
        this.selectedGateIds = [];
        this.gates = [];
    }

    // Toggle Gate Selection
    toggleGateSelection(gateId: string): void {
        const index = this.selectedGateIds.indexOf(gateId);
        if (index > -1) {
            // Remove if already selected
            this.selectedGateIds.splice(index, 1);
        } else {
            // Add if not selected
            this.selectedGateIds.push(gateId);
        }
    }

    // Check if gate is selected
    isGateSelected(gateId: string): boolean {
        return this.selectedGateIds.includes(gateId);
    }

    // PUT - Assign Gates to User
    async assignGates(): Promise<void> {
        if (!this.selectedUserForGate) {
            return;
        }

        this.isAssigningGates = true;

        try {
            const payload = {
                gates: this.selectedGateIds,
            };

            await this.apiService.put(
                `/api/v1/manage/user/${this.selectedUserForGate.id}/assign-gate`,
                payload
            );

            await Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Gate berhasil di-assign ke user',
                confirmButtonColor: '#28a745',
                timer: 2000,
                showConfirmButton: false,
            });

            this.closeGateModal();
            this.getUser(); // Refresh user list
        } catch (error: any) {
            console.error('Error assigning gates:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Gagal',
                text: error?.error?.message || 'Gagal assign gate ke user',
                confirmButtonColor: '#dc3545',
            });
        } finally {
            this.isAssigningGates = false;
        }
    }

    // ==================== MODAL OPERATIONS ====================

    openAddModal(): void {
        this.isEditMode = false;
        this.resetForm();
        this.isModalOpen = true;
    }

    openEditModal(userId: string): void {
        this.getUserById(userId);
    }

    closeModal(): void {
        this.isModalOpen = false;
        this.resetForm();
    }

    hasLoketRole(user: any): boolean {
        return user.roles?.some((role: any) => role.name === 'Loket');
    }

    resetForm(): void {
        this.userForm = {
            name: '',
            username: '',
            email: '',
            password: '',
            status: 'active',
            role_id: null,
        };
        this.selectedUserId = '';
    }

    // ==================== FORM VALIDATION ====================

    validateForm(isEdit: boolean = false): boolean {
        if (
            !this.userForm.name ||
            !this.userForm.username ||
            !this.userForm.email
        ) {
            Swal.fire({
                icon: 'warning',
                title: 'Validasi Gagal',
                text: 'Nama, Username, dan Email wajib diisi',
                confirmButtonColor: '#ffc107',
            });
            return false;
        }

        // Password wajib saat create, optional saat edit
        if (!isEdit && !this.userForm.password) {
            Swal.fire({
                icon: 'warning',
                title: 'Validasi Gagal',
                text: 'Password wajib diisi',
                confirmButtonColor: '#ffc107',
            });
            return false;
        }

        // Validasi email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.userForm.email)) {
            Swal.fire({
                icon: 'warning',
                title: 'Validasi Gagal',
                text: 'Format email tidak valid',
                confirmButtonColor: '#ffc107',
            });
            return false;
        }

        return true;
    }

    // ==================== HANDLER SUBMIT ====================

    async onSubmit(): Promise<void> {
        if (this.isEditMode) {
            await this.updateUser();
        } else {
            await this.createUser();
        }
    }

    // ==================== GET ROLES ====================

    async getRoles(): Promise<void> {
        try {
            const response = await this.apiService.get(
                '/api/v1/manage/role/list'
            );
            // console.log(response);
            this.roles = response.data.list || [];
        } catch (error) {
            console.error('Error fetching roles:', error);
        }
    }

    // ==================== HELPER METHODS ====================

    // Get assigned gates names
    getAssignedGatesNames(user: User): string {
        if (!user.gates || user.gates.length === 0) {
            return '-';
        }
        return user.gates.map((gate) => gate.name).join(', ');
    }
}
